"use client";

// ── where the person was (MOVING-IN-PLAN.md §14) ────────────────────────────
//
// John, 2026-10-24: "if somebody starts an import and they go back and do
// something else, and then they come back, is it at the same place it should be?
// … it needs to remember what the user was doing in most places … in a more
// intentional way." One shared way for a surface to keep its place, instead of
// each one inventing its own sessionStorage key:
//
//   useResumable(key, initial)  state that is still there when the person comes
//                               back: after closing a sheet, moving to another
//                               space, a reload, another tab of the same browser.
//   forgetResumable(key)        the flow finished (or was let go): its place goes.
//
// What is kept here is the person's PLACE (a lane, a step, a choice, a draft of
// their own words), never memory itself: anything that must survive another
// device lives on the server (a staged move-in is its run, lib/intake/stage). It
// is per browser, it ages out (14 days by default), and a storage that throws
// (a private window) only means the place is not kept.

import { useCallback, useEffect, useRef, useState } from "react";
import { placeOf as placeNameOf } from "./places";

const PREFIX = "innernet:resume:";
/** whose places these are (not under PREFIX: it is not a place) */
const OWNER = "innernet:resume-owner";
const DEFAULT_TTL_MS = 14 * 24 * 60 * 60 * 1000;

interface Kept<T> {
  v: 1;
  at: number;
  value: T;
}

function read<T>(key: string, ttlMs: number): T | undefined {
  try {
    const raw = window.localStorage.getItem(PREFIX + key);
    if (!raw) return undefined;
    const k = JSON.parse(raw) as Kept<T>;
    if (k?.v !== 1 || typeof k.at !== "number" || Date.now() - k.at > ttlMs) {
      window.localStorage.removeItem(PREFIX + key);
      return undefined;
    }
    return k.value;
  } catch {
    return undefined;
  }
}

function write<T>(key: string, value: T): void {
  try {
    window.localStorage.setItem(PREFIX + key, JSON.stringify({ v: 1, at: Date.now(), value } satisfies Kept<T>));
  } catch {
    /* a full or blocked storage: the place is not kept, nothing else changes */
  }
}

/** The place is gone: a finished or let-go flow. */
export function forgetResumable(key: string): void {
  try {
    window.localStorage.removeItem(PREFIX + key);
  } catch {
    /* nothing kept, nothing to forget */
  }
}

/**
 * Keep a place from outside a component (a scroll handler, the shell): the same
 * record the hook keeps. An empty value ('' or undefined) forgets it instead, so
 * a draft that was cleared does not come back.
 */
export function keepResumable<T>(key: string, value: T | undefined): void {
  if (typeof window === "undefined") return;
  if (value === undefined || value === "") forgetResumable(key);
  else write(key, value);
}

/** every key this browser keeps a place under, the prefix taken off (a storage that throws: none) */
function keptKeys(): string[] {
  const out: string[] = [];
  try {
    const ls = window.localStorage;
    for (let i = 0; i < ls.length; i++) {
      const k = ls.key(i);
      if (k && k.startsWith(PREFIX)) out.push(k.slice(PREFIX.length));
    }
  } catch {
    /* nothing kept that can be read */
  }
  return out;
}

/** Every place kept in this browser, newest first ("where was i"). One that aged out is let go as it is read. */
export function listResumable(ttlMs = DEFAULT_TTL_MS): Array<{ key: string; at: number; value: unknown }> {
  if (typeof window === "undefined") return [];
  const out: Array<{ key: string; at: number; value: unknown }> = [];
  for (const key of keptKeys()) {
    try {
      const raw = window.localStorage.getItem(PREFIX + key);
      const k = raw ? (JSON.parse(raw) as Kept<unknown>) : null;
      if (!k || k.v !== 1 || typeof k.at !== "number" || Date.now() - k.at > ttlMs) {
        forgetResumable(key);
        continue;
      }
      out.push({ key, at: k.at, value: k.value });
    } catch {
      forgetResumable(key);
    }
  }
  return out.sort((a, b) => b.at - a.at);
}

/**
 * Every place goes: at sign-out and when an account ends, so an unsent line, a name
 * being typed or where a page was read never waits for the next person on a shared
 * browser. The keys carry no user id: what keeps a place a person's own is this, and
 * claimResumable, which forgets them when another person (or nobody) is here.
 */
export function forgetAllResumable(): void {
  if (typeof window === "undefined") return;
  for (const key of keptKeys()) forgetResumable(key);
  try {
    window.localStorage.removeItem(OWNER);
  } catch {
    /* nothing kept, nothing to forget */
  }
}

/** Every place a person left, but not the ones that belong to no one (the docs page, read signed out too). */
export function forgetPersonalResumable(): void {
  if (typeof window === "undefined") return;
  for (const key of keptKeys()) if (placeNameOf(key) !== "docs") forgetResumable(key);
}

/** A label for one person on this browser, from what the page already knows (their address, never kept as it is). Pure. */
export function personKey(address: string): string {
  // cyrb53: a short, stable, non-cryptographic hash; it only tells two people apart on one browser
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  const text = address.trim().toLowerCase();
  for (let i = 0; i < text.length; i++) {
    const c = text.charCodeAt(i);
    h1 = Math.imul(h1 ^ c, 2654435761);
    h2 = Math.imul(h2 ^ c, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return `p${(4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(36)}`;
}

/**
 * The places kept in this browser are one person's: say who is here (`personKey`) and
 * what another person, or nobody, left is forgotten before it can be read, whatever
 * ended their visit (a sign-out, a session that ran out, an account that ended, a
 * different person signing in). `null`: nobody is signed in. Call it before the
 * places are read (a client's first render): the places themselves are read after
 * mount, so a claim made while rendering is always first.
 */
export function claimResumable(who: string | null): void {
  if (typeof window === "undefined") return;
  try {
    const ls = window.localStorage;
    // the same person again: their places are theirs, kept
    if (who !== null && ls.getItem(OWNER) === who) return;
    // another person, nobody, or places from before anyone was named: not for this person to read
    forgetPersonalResumable();
    if (who === null) ls.removeItem(OWNER);
    else ls.setItem(OWNER, who);
  } catch {
    /* a storage that throws keeps no places, so none can be read */
  }
}

/** What a surface left, without holding it (a list that says "you were here"). */
export function peekResumable<T>(key: string, ttlMs = DEFAULT_TTL_MS): T | undefined {
  if (typeof window === "undefined") return undefined;
  return read<T>(key, ttlMs);
}

/**
 * State that survives the person leaving and coming back. The first render uses
 * `initial` (the server's render and the first paint agree), and what was kept
 * is taken right after; every change is kept. `key` null keeps nothing.
 */
export function useResumable<T>(key: string | null, initial: T, opts: { ttlMs?: number } = {}): [T, (next: T | ((was: T) => T)) => void, () => void] {
  const ttl = opts.ttlMs ?? DEFAULT_TTL_MS;
  const [value, setValue] = useState<T>(initial);
  // the first `initial` is the one a fresh key starts from (a caller's literal is a new object each render)
  const start = useRef(initial);
  const taken = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (taken.current === key) return;
    const first = taken.current === undefined;
    taken.current = key;
    // another key is another place: what it kept, or a fresh start, never the last key's value
    const kept = key ? read<T>(key, ttl) : undefined;
    // storage is read only after mount (the server's render and the first paint agree)
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (kept !== undefined) setValue(kept);
    else if (!first) setValue(start.current);
  }, [key, ttl]);
  const set = useCallback(
    (next: T | ((was: T) => T)) => {
      setValue((was) => {
        const v = typeof next === "function" ? (next as (was: T) => T)(was) : next;
        if (key) write(key, v);
        return v;
      });
    },
    [key],
  );
  // the place goes, here too: what is shown starts again as it would for a fresh key
  const forget = useCallback(() => {
    if (key) forgetResumable(key);
    setValue(start.current);
  }, [key]);
  return [value, set, forget];
}
